import React, { FC, useEffect } from 'react';
import { useLocation, useParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { ServicesCartContainer } from '@components/organisms/ServicesCartContainer/ServicesCartContainer';
import { Anchor, AnchorMode } from '@components/atoms/Anchor';
import { ServiceTable } from '@components/molecules/ServiceTable';
import { SITE_URLS } from '@constants';
import { useData } from '@hooks/useData';
import { useServicesCart } from '@hooks/useServicesCart';
import { useCustomNavigate } from '@hooks/useCustomNavigate';
import { useGetServicesCartTotals } from '@services/useGetServicesCartTotals';
import { useGetServicesToBook } from '@services/useGetServicesToBook';

const CURRENT_STEP = 1;

export const ServicesCart: FC = () => {
    const location = useLocation();
    const { navigate } = useCustomNavigate();
    const { request: requestServicesToBook } = useGetServicesToBook();
    const { request: requestServicesCartTotals } = useGetServicesCartTotals();
    const { login, servicesToBook, isLoadingServicesToBook, setCreatedReservations } = useData();
    const {
        storeUuid,
        setStoreUuid,
        setStoreName,
        services,
        setInitialServices,
        displayWorkerStep,
        setScheduleType,
        resetServices,
    } = useServicesCart();
    const { serviceUuid } = useParams();
    const { t, i18n } = useTranslation();

    const translations = {
        title: t('services-cart-component.title'),
        nextButton: t('services-cart-component.next.button'),
        previousButton: t('services-cart-component.previous.button'),
    };

    // reset data
    useEffect(() => {
        setCreatedReservations(null);
        resetServices();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // if user is not logged in, he can't access this page
    useEffect(() => {
        if (login === null) {
            const redirectTo = encodeURIComponent(location.pathname + location.search);
            navigate(`/${SITE_URLS.login}?redirectTo=${redirectTo}`);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [login]);

    // request store and services, when entering page
    useEffect(() => {
        requestServicesToBook({ uuid: serviceUuid || '' });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [i18n.language]);

    const requestUpdateTotals = () => {
        if (storeUuid) {
            requestServicesCartTotals(
                { uuid: storeUuid },
                {
                    services: services
                        ?.filter((service) => service.checked === true && service.quantity > 0)
                        .map((service) => {
                            return { uuid: service.uuid, quantity: service.quantity };
                        }),
                }
            );
        }
    };

    useEffect(() => {
        if (servicesToBook?.data?.merchant?.uuid) {
            setStoreUuid(servicesToBook.data.merchant.uuid);
            setStoreName(servicesToBook.data.merchant.name || '');

            setInitialServices(
                (servicesToBook.data.services || []).map((service) => {
                    return {
                        uuid: service.uuid || '',
                        // check the service whose button was clicked
                        checked: service.uuid === serviceUuid ? true : undefined,
                    };
                })
            );

            // define type of scheduling
            // to know which service book route to take (prebook or pay)
            if (servicesToBook.data.schedule_type) {
                setScheduleType(servicesToBook.data.schedule_type === 'pre_book' ? 'preBook' : 'bookAndPay');
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [servicesToBook?.data]);

    // request totals when changing selection of services
    useEffect(() => {
        requestUpdateTotals();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [services]);

    return (
        <ServicesCartContainer
            step={CURRENT_STEP}
            title={translations.title}
            action={
                <div className="d-grid gap-3">
                    <Anchor
                        href={displayWorkerStep ? SITE_URLS.servicesBookingEmployees : SITE_URLS.servicesBookingScheduling}
                        mode={AnchorMode.primary}
                        disabled={
                            // no services loaded from api
                            servicesToBook?.data?.services?.length === 0 ||
                            // no services checked
                            services?.find((item) => item.checked === true) === undefined
                        }>
                        {translations.nextButton}
                    </Anchor>

                    <Anchor
                        href={SITE_URLS.merchantDetail.replace(':uuid', `${storeUuid}`)}
                        mode={AnchorMode.primary}
                        outline={true}>
                        {translations.previousButton}
                    </Anchor>
                </div>
            }>
            <>{!isLoadingServicesToBook && servicesToBook?.data?.services && <ServiceTable />}</>
        </ServicesCartContainer>
    );
};
